import { useEffect, useRef, useState } from "react";
import Skyline from "../components/Skyline.jsx";
import { SITE } from "../data/site.js";
import logo from "../assets/cllxtv-logo.png";

const POLL_MS = 2500;

export default function Lock({ onGuest, onSignedIn }) {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [showGuest, setShowGuest] = useState(false);
  const [code, setCode] = useState("");
  const [name, setName] = useState("");

  const requestId = useRef(null);
  const timer = useRef(null);

  // Once the link is emailed, watch for it being opened. It will usually be
  // opened in a different browser — Mail hands links to Safari, not to an
  // installed PWA — so this tab claims its own session when approval lands.
  useEffect(() => {
    if (!sent || !requestId.current) return;

    const tick = async () => {
      try {
        const r = await fetch(
          "/api/auth/email/poll?requestId=" + encodeURIComponent(requestId.current),
        );
        if (r.status === 410) {
          setError("That link expired. Send yourself a new one.");
          setSent(false);
          return;
        }
        const d = await r.json();
        if (d.status === "ready") {
          onSignedIn();
          return;
        }
      } catch {
        /* offline or a blip — keep waiting */
      }
      timer.current = setTimeout(tick, POLL_MS);
    };

    timer.current = setTimeout(tick, POLL_MS);
    return () => clearTimeout(timer.current);
  }, [sent, onSignedIn]);

  const sendLink = async () => {
    if (!email.trim() || busy) return;
    setBusy(true);
    setError("");
    try {
      const r = await fetch("/api/auth/email/start", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });
      const d = await r.json();
      if (!r.ok) {
        setError(d.error || "Could not send the link.");
      } else {
        requestId.current = d.requestId;
        setSent(true);
      }
    } catch {
      setError("Could not reach the server. Check your connection.");
    }
    setBusy(false);
  };

  const enterAsGuest = async () => {
    setError("");
    const ok = await onGuest({ code: code.trim(), name: name.trim() });
    if (!ok) setError("That guest code didn't work — check with your leader.");
  };

  return (
    <div className="lock">
      <div className="skyline-dark">
        <Skyline tone="#FFFFFF" far={0.35} near={0.7} />
      </div>
      <div className="inner">
        <img className="logo" src={logo} alt="CLLXTV logo" />
        <div className="eyebrow" style={{ color: "#A9A9A9" }}>
          The Room · Internal Hub
        </div>
        <h1 className="display">{SITE.name}</h1>
        <div className="tag">{SITE.tagline}</div>

        <div style={{ marginTop: 44, maxWidth: 380 }}>
          {sent ? (
            <>
              <div className="sent-card">
                <div className="sent-eyebrow">Check your email</div>
                <p>
                  We sent a link to <b>{email.trim()}</b>. Tap it and you're in — this
                  page will catch up on its own.
                </p>
                <div className="sent-wait">
                  <span className="dot" />
                  <span className="dot" />
                  <span className="dot" />
                  <span>Waiting for you to tap the link…</span>
                </div>
              </div>
              <button
                type="button"
                className="lockout"
                style={{ color: "#D8D8D8", borderColor: "#3A3A3A" }}
                onClick={() => {
                  setSent(false);
                  requestId.current = null;
                }}
              >
                Use a different email
              </button>
            </>
          ) : (
            <>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  sendLink();
                }}
                style={{ display: "flex", flexDirection: "column", gap: 10 }}
              >
                <input
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  aria-label="Email address"
                  placeholder="you@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <button className="btn-y" type="submit" disabled={!email.trim() || busy}>
                  {busy ? "Sending…" : "Email me a link"}
                </button>
              </form>
              <div className="lockteam-label" style={{ marginTop: 18 }}>
                No password. We'll email you a link that signs you in — and we'll match
                you to Planning Center automatically.
              </div>
            </>
          )}

          {error && (
            <div className="err" style={{ marginTop: 14 }}>
              {error}
            </div>
          )}

          <div className="lock-or">
            <span>or</span>
          </div>

          <a
            className="lockout"
            style={{
              display: "block",
              textAlign: "center",
              textDecoration: "none",
              color: "#D8D8D8",
              borderColor: "#3A3A3A",
            }}
            href="/api/auth/login"
          >
            Continue with Planning Center
          </a>

          {showGuest ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                enterAsGuest();
              }}
              style={{ marginTop: 18, display: "flex", flexDirection: "column", gap: 10 }}
            >
              <input
                aria-label="First name"
                placeholder="First name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <input
                className="code"
                aria-label="Guest code"
                placeholder="GUEST CODE"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <button
                type="button"
                className="lockout"
                style={{ marginTop: 0, color: "#D8D8D8", borderColor: "#3A3A3A" }}
                onClick={enterAsGuest}
              >
                Enter as guest
              </button>
            </form>
          ) : (
            <button
              type="button"
              className="lock-link"
              onClick={() => setShowGuest(true)}
            >
              Visiting? Enter a guest code
            </button>
          )}

          <div className="lockteam-label" style={{ marginTop: 24 }}>
            New to CLLXTV? Use the email your church records are under — if we can't find
            you, you can still get in and connect your account later.
          </div>
        </div>
      </div>
    </div>
  );
}
